import type { useTranslations } from "next-intl";

import { uploadCreativeTemplateAsset } from "@/app/_api/creativeTemplates";
import type { FlowTemplateSchema } from "@/app/_api/flowTemplatesServer";
import { HttpError } from "@utilities/api";

import type { ContentWorkflowStep, WorkflowInputType, WorkflowResultType } from "../types";
import { buildFlowTemplateClaimUrl, buildFlowTemplateRedirectUrl } from "./flowTemplateUrls";
import { buildFlowInputs, runFlowAndAwaitResult } from "./runFlowAndAwaitResult";

type Translate = ReturnType<typeof useTranslations<"ClientKeys.creative_templates">>;

export interface FlowSubmitForm {
  files: Record<string, File>;
  texts: Record<string, string>;
  prefilledAssetsByPort: Record<string, { assetId: string; previewUrl: string }>;
}

/**
 * Analytics adapter for {@link runFlowSubmit}. The engine decides *when* each
 * event fires and computes `input_type` / `error_type` / `error_message`; the
 * adapter decides the event name + per-surface base props (the page sends
 * template_category/variant_slug/generation_model, the block sends `source`).
 *
 * `onQuotaExceeded.phase` distinguishes the video early-redirect from a caught
 * quota/rate-limit error — the page intentionally omits `generation_model` on
 * the former, so the adapter needs to tell them apart to stay byte-identical.
 */
export interface FlowSubmitAnalytics {
  onStarted(extra: { input_type: WorkflowInputType }): void;
  onSucceeded(extra: { input_type: WorkflowInputType }): void;
  onQuotaExceeded(extra: { error_type: string; phase: "video_redirect" | "error" }): void;
  onFailed(extra: { error_type?: string; error_message?: string }): void;
}

export interface RunFlowSubmitParams {
  form: FlowSubmitForm;
  flowTemplate: FlowTemplateSchema;
  resultType: WorkflowResultType;
  signal: AbortSignal;
  t: Translate;
  setStep: (step: ContentWorkflowStep) => void;
  setError: (message: string) => void;
  setResultUrl: (url: string) => void;
  setRedirectCtaUrl: (url: string) => void;
  /** Page wraps redirect/claim URLs through sign-up; the block uses raw URLs. */
  toCtaUrl?: (url: string) => string;
  /**
   * Runs once, synchronously, right before the "started" event — the page uses
   * it for hero-media object-URL bookkeeping + `setUserInput`; the block omits
   * it (its form card owns previews).
   */
  onStart?: (form: FlowSubmitForm) => void;
  analytics: FlowSubmitAnalytics;
}

/**
 * Shared run controller for a Flow Template submit, used by both the
 * CreativeTemplate omnibox (`useCreativeTemplate`) and the standalone block
 * (`useFlowTemplateRun`): upload each port's asset → run the template against xi
 * → expose the result, mapping every external-API failure to a step + message.
 *
 * Kept as a plain async function (not a hook) so each caller keeps owning its
 * own state, refs and AbortController and just delegates the procedure here.
 */
export async function runFlowSubmit({
  form,
  flowTemplate,
  resultType,
  signal,
  t,
  setStep,
  setError,
  setResultUrl,
  setRedirectCtaUrl,
  toCtaUrl = url => url,
  onStart,
  analytics,
}: RunFlowSubmitParams) {
  const fileEntries = Object.entries(form.files);
  const firstFile = fileEntries[0]?.[1];
  const firstPrefilledAsset = Object.values(form.prefilledAssetsByPort)[0];
  const inputType: WorkflowInputType = firstFile || firstPrefilledAsset ? "file" : "text";

  onStart?.(form);
  analytics.onStarted({ input_type: inputType });

  // Seed with prefilled examples; user-uploaded files overwrite below so a real
  // upload always wins on a colliding port.
  let assetIdsByPort: Record<string, string> = Object.fromEntries(
    Object.entries(form.prefilledAssetsByPort).map(([portId, asset]) => [portId, asset.assetId]),
  );

  try {
    if (fileEntries.length > 0) {
      setStep("uploading");
      // Per-port uploads run in parallel; the shared abort signal stays
      // unaborted unless the user cancelled — the outer catch's signal.aborted
      // check depends on that distinction.
      const uploaded = await Promise.all(
        fileEntries.map(async ([portId, file]) => {
          const { asset_id } = await uploadCreativeTemplateAsset(file, signal);
          return [portId, asset_id] as const;
        }),
      );
      if (signal.aborted) return;
      assetIdsByPort = { ...assetIdsByPort, ...Object.fromEntries(uploaded) };
    }

    // Video templates require an account — skip the run entirely and route the
    // visitor to the xi app with their inputs pre-seeded.
    if (resultType === "video") {
      analytics.onQuotaExceeded({ error_type: "rate_limited", phase: "video_redirect" });
      setRedirectCtaUrl(
        toCtaUrl(buildFlowTemplateRedirectUrl({ flowTemplate, assetIdsByPort, textsByPort: form.texts })),
      );
      setStep("rate_limited");
      return;
    }

    setStep("generating");

    const inputs = buildFlowInputs({ assetIdsByPort, textsByPort: form.texts });

    const flowResult = await runFlowAndAwaitResult({ flowTemplate, inputs, resultType, signal });

    if (signal.aborted) return;

    analytics.onSucceeded({ input_type: inputType });
    setRedirectCtaUrl(toCtaUrl(buildFlowTemplateClaimUrl({ flowTemplate, sessionId: flowResult.sessionId })));
    setResultUrl(flowResult.resultUrl);
    setStep("result");
  } catch (err) {
    if (signal.aborted) return;

    const errorType = err instanceof HttpError ? err.errorType : null;

    switch (errorType) {
      case "image_too_large":
        analytics.onFailed({ error_type: errorType });
        setError(t("error_image_too_large"));
        setStep("idle");
        break;

      case "generation_refused":
      case "cms_unreachable":
        analytics.onFailed({ error_type: errorType });
        setError(t(errorType === "generation_refused" ? "error_generation_refused" : "error_cms_unreachable"));
        setStep("idle");
        break;

      case "quota_exceeded":
      case "rate_limited":
        analytics.onQuotaExceeded({ error_type: errorType, phase: "error" });
        setRedirectCtaUrl(
          toCtaUrl(buildFlowTemplateRedirectUrl({ flowTemplate, assetIdsByPort, textsByPort: form.texts })),
        );
        setStep(errorType === "rate_limited" ? "rate_limited" : "quota_exceeded");
        break;

      default:
        analytics.onFailed({ error_message: err instanceof Error ? err.message : "Unknown error" });
        setError(t("error_generic"));
        setStep("idle");
    }
  }
}

/**
 * Builds a max-file-size validator shared by both Flow Template surfaces. A
 * limit of `0` disables the check — the page derives the limit from CMS config
 * (with a flow-template fallback), the block hardcodes a safe default.
 *
 * This is a fast pre-upload UX guard only: the xi upload API independently
 * enforces the real size limit (it's the source of the `image_too_large` error
 * mapped above), so a bypassed client check can't smuggle an oversized file
 * through. Video *duration* is intentionally not validated client-side.
 */
export function createFlowFileValidator({ maxFileSizeMb, t }: { maxFileSizeMb: number; t: Translate }) {
  return (file: File) => {
    if (maxFileSizeMb > 0 && file.size > maxFileSizeMb * 1024 * 1024) {
      return t("error_file_too_large", { maxSize: maxFileSizeMb });
    }
    return null;
  };
}
